import React, { useState } from 'react';
import eyeIcon from '../assets/eye.png';
import lineChartIcon from '../assets/line-chart.png';
import barLineChartIcon from '../assets/bar-line-chart.png';
import barChartCircleIcon from '../assets/bar-chart-circle.png';
import cubeIcon from '../assets/cube.png';
import algorithmIcon from '../assets/algorithm.png';
import clockIcon from '../assets/clock.png';
import folderCheckIcon from '../assets/folder-check.png';
import gitBranchIcon from '../assets/git-branch.png';
import { Modal } from './Modal';
import { ParameterDiagramModal } from './ParameterDiagramModal';
import type { SAIFileResponse } from '../services/api';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  LineController,
  ScatterController,
} from 'chart.js';
import { Scatter } from 'react-chartjs-2';

ChartJS.register(
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  LineController,
  ScatterController
);

interface CustomModelResultsProps {
  trainingResult?: any;
  selectedSAIFile?: SAIFileResponse | null;
  onBack: () => void;
  onRejectTryDifferent: () => void;
  onRejectAdjustParams: () => void;
  onAcceptModel: (modelName: string, remarks: string) => void;
}

export const CustomModelResults: React.FC<CustomModelResultsProps> = ({
  trainingResult,
  selectedSAIFile,
  onBack,
  onRejectTryDifferent,
  onRejectAdjustParams,
  onAcceptModel,
}) => {
  const [isAcceptModalOpen, setIsAcceptModalOpen] = useState(false);
  const [isDiagramModalOpen, setIsDiagramModalOpen] = useState(false);
  const [modelName, setModelName] = useState('');
  const [remarks, setRemarks] = useState('');

  const diagramImageUrl = selectedSAIFile?.image ?? null;

  const handleAcceptClick = () => {
    setIsAcceptModalOpen(true);
  };

  const handleModalClose = () => {
    setIsAcceptModalOpen(false);
    setModelName('');
    setRemarks('');
  };

  const handleAcceptModelSubmit = () => {
    if (!modelName.trim()) {
      return;
    }
    onAcceptModel(modelName.trim(), remarks.trim());
    handleModalClose();
  };

  const getAlgorithmTitle = () => {
    if (trainingResult?.additional_params?.type) {
      const algoType = trainingResult.additional_params.type.toLowerCase();
      switch (algoType) {
        case 'random forest':
          return 'Random Forest';
        case 'support vector':
          return 'Support Vector Machine';
        case 'neural network':
          return 'Neural Network';
        case 'gradient boosting':
          return 'Gradient Boosting';
        default:
          return algoType;
      }
    }
    return 'ML Algorithm';
  };

  // Convert pred_values and actual_values to scatter plot format
  const convertToScatterData = (actual: number[] | undefined, predicted: number[] | undefined) => {
    if (!actual || !predicted || actual.length !== predicted.length) {
      return [];
    }
    return actual.map((act, index) => ({
      x: act,
      y: predicted[index],
    }));
  };

  const testScatterData = convertToScatterData(
    trainingResult?.test_metric?.actual_values,
    trainingResult?.test_metric?.pred_values
  );

  const trainScatterData = convertToScatterData(
    trainingResult?.train_metric?.actual_values,
    trainingResult?.train_metric?.pred_values
  );

  // Calculate axis limits based on output parameter's min/max ± 10%
  const getAxisLimits = () => {
    // Find the output parameter from selectedSAIFile
    const outputParam = selectedSAIFile?.parameters?.find(
      param => (param.type || '').toLowerCase() === 'output'
    );

    if (outputParam && outputParam.min != null && outputParam.max != null) {
      const range = outputParam.max - outputParam.min;
      const padding = range * 0.1; // 10% padding
      return {
        min: outputParam.min - padding,
        max: outputParam.max + padding,
      };
    }

    // Fallback to data-based limits if no output parameter found
    const allValues = [
      ...testScatterData.map(point => point.x),
      ...testScatterData.map(point => point.y),
      ...trainScatterData.map(point => point.x),
      ...trainScatterData.map(point => point.y),
    ];
    return {
      min: allValues.length > 0 ? Math.floor(Math.min(...allValues)) : 0,
      max: allValues.length > 0 ? Math.ceil(Math.max(...allValues)) : 1,
    };
  };

  const { min, max } = getAxisLimits();

  return (
    <div className="wizard-step-container">
      <div className="wizard-step-header">
        <button className="back-button" onClick={onBack}>
          <span className="back-arrow">←</span> Back
        </button>
        <h1>Results</h1>
        <button
          className="btn btn-secondary view-button"
          onClick={() => setIsDiagramModalOpen(true)}
        >
          <img src={eyeIcon} alt="View" width="20" height="20" />
          View Parameter Diagram
        </button>
      </div>

      <div className="results-success-banner">
        <div className="success-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <div className="success-message">
          <strong>Model training completed successfully!</strong> Review the performance metrics below.
        </div>
      </div>

      <div className="results-content">
        <div className="results-top">
          <section className="results-section">
            <h3>Test Data</h3>
            <p>Data used for model evaluation</p>
            <div className="metrics-grid-row">
              <div className="metric-card">
                <div className="metric-icon metric-blue">
                  <img src={lineChartIcon} alt="Line Chart" width="24" height="24" />
                </div>
                <div className="metric-info">
                  <div className="metric-label">Mean Absolute Error</div>
                  <div className="metric-value">{trainingResult.test_metric.mean_absolute_error?.toFixed(5)}</div>
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-icon metric-blue">
                  <img src={barLineChartIcon} alt="Bar Line Chart" width="24" height="24" />
                </div>
                <div className="metric-info">
                  <div className="metric-label">Max Absolute Error</div>
                  <div className="metric-value">{trainingResult.test_metric.max_absolute_error?.toFixed(5)}</div>
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-icon metric-blue">
                  <img src={barChartCircleIcon} alt="Bar Chart Circle" width="24" height="24" />
                </div>
                <div className="metric-info">
                  <div className="metric-label">Mean Squared Error</div>
                  <div className="metric-value">{trainingResult.test_metric.mean_squared_error?.toFixed(5)}</div>
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-icon metric-blue">
                  <img src={cubeIcon} alt="Cube" width="24" height="24" />
                </div>
                <div className="metric-info">
                  <div className="metric-label">R² Score</div>
                  <div className="metric-value">{trainingResult.test_metric.r2_score?.toFixed(5)}</div>
                </div>
              </div>
            </div>
          </section>

          <section className="results-section">
            <h3>Train Data</h3>
            <p>Data used for model training</p>
            <div className="metrics-grid-row">
              <div className="metric-card">
                <div className="metric-icon metric-green">
                  <img src={lineChartIcon} alt="Line Chart" width="24" height="24" />
                </div>
                <div className="metric-info">
                  <div className="metric-label">Mean Absolute Error</div>
                  <div className="metric-value">{trainingResult.train_metric.mean_absolute_error?.toFixed(5)}</div>
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-icon metric-green">
                  <img src={barLineChartIcon} alt="Bar Line Chart" width="24" height="24" />
                </div>
                <div className="metric-info">
                  <div className="metric-label">Max Absolute Error</div>
                  <div className="metric-value">{trainingResult.train_metric.max_absolute_error?.toFixed(5)}</div>
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-icon metric-green">
                  <img src={barChartCircleIcon} alt="Bar Chart Circle" width="24" height="24" />
                </div>
                <div className="metric-info">
                  <div className="metric-label">Mean Squared Error</div>
                  <div className="metric-value">{trainingResult.train_metric.mean_squared_error?.toFixed(5)}</div>
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-icon metric-green">
                  <img src={cubeIcon} alt="Cube" width="24" height="24" />
                </div>
                <div className="metric-info">
                  <div className="metric-label">R² Score</div>
                  <div className="metric-value">{trainingResult.train_metric.r2_score?.toFixed(5)}</div>
                </div>
              </div>
            </div>
          </section>
        </div>

        <div className="results-bottom">
          <div className="results-summary-left">
            <section className="results-section">
              <h3>Training Summary</h3>
              <div className="training-summary-column">
                <div className="metric-card">
                  <div className="metric-icon metric-green">
                    <img src={algorithmIcon} alt="Algorithm" width="24" height="24" />
                  </div>
                  <div className="metric-info">
                    <div className="metric-label">Algorithm</div>
                    <div className="metric-value">{getAlgorithmTitle()}</div>
                  </div>
                </div>
                <div className="metric-card">
                  <div className="metric-icon metric-green">
                    <img src={clockIcon} alt="Clock" width="24" height="24" />
                  </div>
                  <div className="metric-info">
                    <div className="metric-label">Training Time</div>
                    <div className="metric-value">{trainingResult?.training_time?.toFixed(2)}s</div>
                  </div>
                </div>
                <div className="metric-card">
                  <div className="metric-icon metric-green">
                    <img src={folderCheckIcon} alt="Folder Check" width="24" height="24" />
                  </div>
                  <div className="metric-info">
                    <div className="metric-label">Training Samples</div>
                    <div className="metric-value">{trainingResult?.datapoint_count} records</div>
                  </div>
                </div>
                <div className="metric-card">
                  <div className="metric-icon metric-green">
                    <img src={gitBranchIcon} alt="Git Branch" width="24" height="24" />
                  </div>
                  <div className="metric-info">
                    <div className="metric-label">Test Data Split</div>
                    <div className="metric-value">{trainingResult?.additional_params?.test_train_data_split * 100}%</div>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <div className="results-summary-right">
            <section className="results-section">
              <h3>Predicted vs Actual</h3>
              <div className="chart-placeholder" style={{ maxWidth: '750px', margin: '0 auto' }}>
                <Scatter
                  data={{
                    datasets: [
                      {
                        label: 'Test data',
                        data: testScatterData,
                        backgroundColor: '#7C8DC6',
                        borderColor: '#344FA7',
                        pointRadius: 5,
                        pointHoverRadius: 8,
                      },
                      {
                        label: 'Train data',
                        data: trainScatterData,
                        backgroundColor: '#89D889',
                        borderColor: '#29B829',
                        pointRadius: 5,
                        pointHoverRadius: 8,
                      },
                      {
                        type: 'line',
                        label: 'Perfect prediction',
                        data: [{ x: min, y: min }, { x: max, y: max }],
                        borderColor: 'rgba(0, 0, 0, 0.8)',
                        borderWidth: 2,
                        pointRadius: 0,
                        fill: false,
                        showLine: true,
                        hidden: false,
                      } as any,
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    aspectRatio: 1,
                    plugins: {
                      legend: {
                        display: true,
                        position: 'chartArea',
                        align: 'start',
                        labels: {
                          usePointStyle: true,
                          pointStyle: 'circle',
                          padding: 30,
                          color: '#000000',
                          font: {
                            size: 16,
                            weight: 'normal',
                            family: 'Lexend',
                          },
                          boxWidth: 12,
                          boxHeight: 12,
                          filter: function(item) {
                            return item.text !== 'Perfect prediction';
                          }
                        },
                      },
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            const x = context.parsed?.x ?? 0;
                            const y = context.parsed?.y ?? 0;
                            return `Actual: ${x.toFixed(4)}, Predicted: ${y.toFixed(4)}`;
                          }
                        }
                      }
                    },
                    scales: {
                      x: {
                        type: 'linear',
                        position: 'bottom',
                        title: {
                          display: true,
                          text: 'Actual',
                          color: '#000000',
                          font: {
                            size: 20,
                            weight: 'normal',
                            family: 'Lexend',
                          },
                        },
                        min: min,
                        max: max,
                        ticks: {
                          stepSize: 1,
                        },
                        grid: {
                          color: 'rgba(229, 231, 235, 0.5)',
                        },
                      },
                      y: {
                        type: 'linear',
                        title: {
                          display: true,
                          text: 'Predicted',
                          color: '#000000',
                          font: {
                            size: 20,
                            weight: 'normal',
                            family: 'Lexend',
                          },
                        },
                        min: min,
                        max: max,
                        ticks: {
                          stepSize: 1,
                        },
                        grid: {
                          color: 'rgba(229, 231, 235, 0.5)',
                        },
                      },
                    },
                  }}
                />
              </div>
            </section>
          </div>
        </div>
      </div>

      <div className="results-actions">
        <button className="btn btn-secondary" onClick={onRejectTryDifferent}>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M15 5L5 15M5 5L15 15" stroke="#3C4858" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Reject Model - Try Different Algorithm
        </button>
        <button className="btn btn-secondary" onClick={onRejectAdjustParams}>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M2.5 6.66675L12.5 6.66675M12.5 6.66675C12.5 8.04746 13.6193 9.16675 15 9.16675C16.3807 9.16675 17.5 8.04746 17.5 6.66675C17.5 5.28604 16.3807 4.16675 15 4.16675C13.6193 4.16675 12.5 5.28604 12.5 6.66675ZM7.5 13.3334L17.5 13.3334M7.5 13.3334C7.5 14.7141 6.38071 15.8334 5 15.8334C3.61929 15.8334 2.5 14.7141 2.5 13.3334C2.5 11.9527 3.61929 10.8334 5 10.8334C6.38071 10.8334 7.5 11.9527 7.5 13.3334Z" stroke="#3C4858" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Reject Model - Adjust Parameters
        </button>
        <button className="btn btn-primary" onClick={handleAcceptClick}>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M16.6663 5L7.49967 14.1667L3.33301 10" stroke="white" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Accept Model - Return to Home
        </button>
      </div>

      <ParameterDiagramModal
        isOpen={isDiagramModalOpen}
        onClose={() => setIsDiagramModalOpen(false)}
        imageUrl={diagramImageUrl}
      />

      <Modal
        isOpen={isAcceptModalOpen}
        onClose={handleModalClose}
        title="Accept Model"
        className="accept-model-modal"
      >
        <div className="accept-model-modal-content">
          <p className="accept-model-instruction">
            To accept this model, please type a name and remarks.
          </p>

          <div className="form-field">
            <label htmlFor="modelName">Model Name*</label>
            <input
              id="modelName"
              type="text"
              value={modelName}
              onChange={(e) => setModelName(e.target.value)}
              placeholder="Model Name"
            />
          </div>

          <div className="form-field">
            <label htmlFor="remarks">Remarks</label>
            <textarea
              id="remarks"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Remarks"
              rows={4}
            />
          </div>

          <div className="modal-footer">
            <button
              className="btn btn-primary modal-save-btn"
              onClick={handleAcceptModelSubmit}
              disabled={!modelName.trim()}
            >
              Accept Model
              <span className="arrow-right">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M4.16699 10H15.8337M15.8337 10L10.0003 4.16669M15.8337 10L10.0003 15.8334" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
